import { useEffect, useState } from 'react'
import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage'

import { SecondaryBase } from './../Commons/SecondaryBase'
import { Confirm } from './../../Commons/Modals/Confirm'


// ==========================================
// DICCIONARIO DE VELOCIDADES PREDEFINIDAS
// ==========================================
const SPEED_PRESETS = {
    slow: { label: '🐢 Lento', minTime: 1000, maxTime: 1500, accelerationMultiplier: 0.98 },
    normal: { label: '🚶 Normal', minTime: 800, maxTime: 1200, accelerationMultiplier: 0.95 },
    fast: { label: '🏃 Rápido', minTime: 600, maxTime: 900, accelerationMultiplier: 0.90 },
    extreme: { label: '🔥 Extremo', minTime: 400, maxTime: 700, accelerationMultiplier: 0.85 },
    custom: { label: '⚙️ Personalizado', minTime: 800, maxTime: 1200, accelerationMultiplier: 0.95 }
};

/**
 * Componente LuchoFestAdmin
 * Panel de control maestro para operar la dinámica Lucho Fest en vivo.
 */
const LuchoFest = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // ==========================================
    // 1. ESTADOS DE DATOS Y LOGÍSTICA
    // ==========================================
    const [activeZonesList, setActiveZonesList] = useState([]);
    const [isLoadingZones, setIsLoadingZones] = useState(true);

    const [isPracticeRound, setIsPracticeRound] = useState(true);
    const [selectedWinningZone, setSelectedWinningZone] = useState('random');
    
    // NUEVO: Estado para pre-calcular y mostrar el ganador al azar ANTES de iniciar
    const [calculatedRandomWinner, setCalculatedRandomWinner] = useState(null);

    // Estatus del sistema y Cronómetro
    const [currentSystemStatus, setCurrentSystemStatus] = useState('Juego no iniciado. Configura la partida.');
    const [gameCountdownTimer, setGameCountdownTimer] = useState(null);

    // ==========================================
    // 2. ESTADOS DEL CONSTRUCTOR DE FASES
    // ==========================================
    const defaultPhasesSequence = [
        { id: 1, type: 'normal', duration: 10, bombs: 2, speedLevel: 'normal', speedConfig: SPEED_PRESETS.normal },
        { id: 2, type: 'bonus', duration: 4, bombs: 0, speedLevel: 'normal', speedConfig: SPEED_PRESETS.normal },
        { id: 3, type: 'normal', duration: 12, bombs: 3, speedLevel: 'fast', speedConfig: SPEED_PRESETS.fast }
    ];

    const [gamePhasesSequence, setGamePhasesSequence] = useState(defaultPhasesSequence);

    // ==========================================
    // 3. ESTADOS PARA EL MODAL DE CONFIRMACIÓN
    // ==========================================
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState(null);

    // ==========================================
    // 4. EFECTOS (Carga y Cronómetro)
    // ==========================================
    useEffect(() => {
        const fetchActiveZones = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}admin/GetActiveZones`);
                if (response.ok) {
                    const zones = await response.json();
                    setActiveZonesList(zones);
                } else {
                    showMessage({ type: MESSAGE_TYPE.WARNING, text: 'No se pudieron cargar las zonas activas.' });
                }
            } catch (error) {
                console.error("Error al cargar zonas activas", error);
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de conexión al cargar las zonas.' });
            } finally {
                setIsLoadingZones(false);
            }
        };

        const savedCustomPhases = localStorage.getItem('luchoFestCustomPhases');
        if (savedCustomPhases) setGamePhasesSequence(JSON.parse(savedCustomPhases));

        fetchActiveZones();
    }, []);

    // NUEVO: Lógica para calcular ganador al azar en cuanto eligen "random" o se cargan las zonas
    const generateRandomWinner = () => {
        if (activeZonesList.length > 0) {
            const randomIndex = Math.floor(Math.random() * activeZonesList.length);
            const randomZoneRaw = activeZonesList[randomIndex];
            setCalculatedRandomWinner(randomZoneRaw.toLowerCase().replace("zona:", "").trim());
        }
    };

    useEffect(() => {
        if (selectedWinningZone === 'random') {
            generateRandomWinner();
        } else {
            setCalculatedRandomWinner(null);
        }
    }, [selectedWinningZone, activeZonesList]);

    // NUEVO: Motor del cronómetro de apoyo
    useEffect(() => {
        let interval = null;
        if (gameCountdownTimer !== null && gameCountdownTimer > 0) {
            interval = setInterval(() => {
                setGameCountdownTimer(prev => prev - 1);
            }, 1000);
        } else if (gameCountdownTimer === 0) {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [gameCountdownTimer]);

    const totalGameDurationInSeconds = gamePhasesSequence.reduce((total, phase) => total + phase.duration, 0);

    // ==========================================
    // 5. MÉTODOS DEL CONSTRUCTOR DE FASES
    // ==========================================
    const updatePhaseProperty = (phaseIdToUpdate, propertyName, newValue) => {
        setGamePhasesSequence(previousSequence => 
            previousSequence.map(phase => 
                phase.id === phaseIdToUpdate ? { ...phase, [propertyName]: newValue } : phase
            )
        );
    };

    // NUEVO: Controlador inteligente de velocidad (Cambia el preset o habilita el modo custom)
    const updatePhaseSpeedPreset = (phaseIdToUpdate, selectedSpeedLevel) => {
        setGamePhasesSequence(previousSequence => 
            previousSequence.map(phase => {
                if (phase.id === phaseIdToUpdate) {
                    return { 
                        ...phase, 
                        speedLevel: selectedSpeedLevel,
                        speedConfig: SPEED_PRESETS[selectedSpeedLevel] 
                    };
                }
                return phase;
            })
        );
    };

    const updateCustomSpeedValue = (phaseIdToUpdate, propertyName, newValue) => {
        setGamePhasesSequence(previousSequence => 
            previousSequence.map(phase => {
                if (phase.id === phaseIdToUpdate) {
                    return { ...phase, speedConfig: { ...phase.speedConfig, [propertyName]: newValue } };
                }
                return phase;
            })
        );
    };

    const addNewPhase = () => {
        const newPhase = { 
            id: Date.now(), type: 'normal', duration: 10, bombs: 2, 
            speedLevel: 'normal', speedConfig: SPEED_PRESETS.normal
        };
        setGamePhasesSequence([...gamePhasesSequence, newPhase]);
    };

    const removePhase = (phaseIdToRemove) => {
        if (gamePhasesSequence.length <= 1) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debe existir al menos una fase en el juego.' });
            return;
        }
        setGamePhasesSequence(gamePhasesSequence.filter(phase => phase.id !== phaseIdToRemove));
    };

    const movePhasePosition = (currentIndex, direction) => {
        if ((currentIndex === 0 && direction === -1) || (currentIndex === gamePhasesSequence.length - 1 && direction === 1)) return;
        const newSequence = [...gamePhasesSequence];
        const targetIndex = currentIndex + direction;
        const temporaryPhase = newSequence[currentIndex];
        newSequence[currentIndex] = newSequence[targetIndex];
        newSequence[targetIndex] = temporaryPhase;
        setGamePhasesSequence(newSequence);
    };

    const saveCustomPhasesToMemory = () => {
        localStorage.setItem('luchoFestCustomPhases', JSON.stringify(gamePhasesSequence));
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Configuración de fases guardada exitosamente.' });
    };

    const loadDefaultPhases = () => {
        setGamePhasesSequence(defaultPhasesSequence);
        localStorage.removeItem('luchoFestCustomPhases');
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Se ha restaurado la configuración predeterminada.' });
    };


    // ==========================================
    // 6. WRAPPERS PARA LA API
    // ==========================================
    const executeDisplayFrontPage = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}admin/displayFrontPageLuchoFest`, { method: 'POST' });
            if (response.ok) {
                setCurrentSystemStatus('Mostrando pantalla de Portada/Espera ⏳');
                setGameCountdownTimer(null); // Reseteamos cronómetro
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Portada enviada exitosamente.' });
                return { success: true };
            }
            return { success: false, message: 'Error en el servidor al enviar portada.' };
        } catch (error) {
            return { success: false, message: 'Error de conexión con la API.' };
        }
    };

    const executeStartGame = async () => {
        const finalWinner = selectedWinningZone === 'random' ? calculatedRandomWinner : selectedWinningZone;

        const configurationPayload = {
            isPracticeRound: isPracticeRound,
            winningZone: finalWinner,
            gameSequence: gamePhasesSequence
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(configurationPayload)
            });

            if (response.ok) {
                setCurrentSystemStatus(`Juego en curso 🟢 (Ganador: ${finalWinner.toUpperCase()})`);
                setGameCountdownTimer(totalGameDurationInSeconds + 3); // Iniciar cronómetro de apoyo
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡El juego ha comenzado en todas las zonas!' });
                return { success: true };
            }
            return { success: false, message: 'Error al intentar iniciar la partida.' };
        } catch (error) {
            return { success: false, message: 'Fallo de red al comunicar con el servidor.' };
        }
    };

    const executeStopGame = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/stop`, { method: 'POST' });
            if (response.ok) {
                setCurrentSystemStatus('Juego detenido o abortado 🛑');
                setGameCountdownTimer(null); // Reseteamos cronómetro
                showMessage({ type: MESSAGE_TYPE.WARNING, text: 'El juego fue abortado. Pantallas en espera.' });
                return { success: true };
            }
            return { success: false, message: 'Error al intentar detener el juego.' };
        } catch (error) {
            return { success: false, message: 'Error de conexión.' };
        }
    };

    const executeEmergencyPromo = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/emergencyPromo`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ targetZones: ["todas"] })
            });
            if (response.ok) {
                setCurrentSystemStatus('Mostrando promoción forzada 🎁');
                setGameCountdownTimer(null);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Promoción comercial disparada con éxito.' });
                return { success: true };
            }
            return { success: false, message: 'Error al lanzar la promoción.' };
        } catch (error) {
            return { success: false, message: 'Error de conexión.' };
        }
    };

    const executeShowWinnerOnScreen = async () => {
        const finalWinner = selectedWinningZone === 'random' ? calculatedRandomWinner : selectedWinningZone;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/screen/showWinner`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ winningZone: finalWinner })
            });
            if (response.ok) {
                setCurrentSystemStatus('Mostrando ganador en pantalla gigante 🏆');
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Gráfico de ganador enviado a la pantalla.' });
                return { success: true };
            }
            return { success: false, message: 'Error al enviar ganador a la pantalla.' };
        } catch (error) {
            return { success: false, message: 'Error de conexión.' };
        }
    };

    const executePracticeFinishedOnScreen = async () => {
        const finalWinner = selectedWinningZone === 'random' ? calculatedRandomWinner : selectedWinningZone;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/screen/showPracticeFinished`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ winningZone: finalWinner })
            });
            if (response.ok) {
                setCurrentSystemStatus('Mostrando Prueba Finalizada en pantalla 🏁');
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Mensaje de prueba finalizada enviado.' });
                return { success: true };
            }
            return { success: false, message: 'Error al enviar mensaje a la pantalla.' };
        } catch (error) {
            return { success: false, message: 'Error de conexión.' };
        }
    };

    const triggerModal = (message, actionFunc, yesBtnText = 'Confirmar', noBtnText = 'Cancelar') => {
        setConfirmModalData({
            message: message, actionToYesButton: actionFunc, textYesButton: yesBtnText, textNoButton: noBtnText
        });
        setShowConfirmModal(true);
    };

    const ReturnToDashboard = () => {
        if (getOutOfTheDynamic) getOutOfTheDynamic();
    };

    // ==========================================
    // RENDERIZADO DEL PANEL
    // ==========================================
    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            
            {/* Header y Estatus General */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
                <p className='AdmRunToGoal__Title mb-3 mb-md-0'>
                    <i className="bi bi-cake2"></i> Lucho Fest
                </p>
                <div className="d-flex align-items-center">
                    {gameCountdownTimer !== null && (
                        <div className={`badge ${gameCountdownTimer === 0 ? 'bg-success' : 'bg-warning text-dark'} fs-5 me-3 px-3 py-2 shadow-sm`}>
                            {gameCountdownTimer > 0 ? `⏱️ Faltan: ${gameCountdownTimer}s` : '✅ ¡Juego Terminado!'}
                        </div>
                    )}
                    <div className="alert alert-info py-2 px-3 mb-0 font-weight-bold shadow-sm" style={{ fontSize: '0.9rem' }}>
                        <i className="bi bi-broadcast me-2"></i> Status: {currentSystemStatus}
                    </div>
                </div>
            </div>

            {/* BLOQUE 1: CONFIGURACIÓN LOGÍSTICA */}
            <div className="card shadow-sm mb-4 border-primary">
                <div className="card-header bg-primary text-white font-weight-bold">
                    <i className="bi bi-gear-fill me-2"></i> 1. Logística de la Ronda
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="font-weight-bold d-block">Tipo de Ronda</label>
                            {isPracticeRound ? (
                                <>
                                    <button type='button' className='Btn__Adsl--Red w-100 mb-2'><i className='bi bi-check-circle-fill me-1'></i> Ronda de prueba</button>
                                    <button type='button' onClick={() => setIsPracticeRound(false)} className='Btn__Adsl--RedOutlineTransparent w-100'>Ronda real (Premio)</button>
                                </>
                            ) : (
                                <>
                                    <button type='button' onClick={() => setIsPracticeRound(true)} className='Btn__Adsl--RedOutlineTransparent w-100 mb-2'>Ronda de prueba</button>
                                    <button type='button' className='Btn__Adsl--Red w-100'><i className='bi bi-check-circle-fill me-1'></i> Ronda real (Premio)</button>
                                </>
                            )}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="font-weight-bold d-block">Elegir Zona Ganadora</label>
                            {isLoadingZones ? (
                                <p className="text-muted small">Cargando zonas...</p>
                            ) : (
                                <div className="input-group">
                                    <span className="input-group-text bg-light"><i className="bi bi-trophy"></i></span>
                                    <select 
                                        className="form-select form-control" 
                                        value={selectedWinningZone}
                                        onChange={(event) => setSelectedWinningZone(event.target.value)}
                                    >
                                        <option value="random">🎲 Elegir al Azar (El sistema decide)</option>
                                        {activeZonesList.map((zoneItem, index) => (
                                            <option key={index} value={zoneItem.replace("zona:", "")}>
                                                {zoneItem.toUpperCase()}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            {/* Pre-visualización del ganador al azar */}
                            {selectedWinningZone === 'random' && calculatedRandomWinner && (
                                <div className="mt-2 p-2 bg-success text-white rounded small d-flex justify-content-between align-items-center shadow-sm">
                                    <span><strong>Pre-calculado:</strong> ZONA {calculatedRandomWinner.toUpperCase()}</span>
                                    <button className="btn btn-sm btn-light py-0 px-2" onClick={generateRandomWinner} title="Volver a lanzar dados">
                                        <i className="bi bi-arrow-repeat"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* BLOQUE 2: CONSTRUCTOR DINÁMICO DE FASES */}
            <div className="card shadow-sm mb-4 border-dark">
                <div className="card-header bg-dark text-white font-weight-bold d-flex justify-content-between align-items-center">
                    <span><i className="bi bi-list-ol me-2"></i> 2. Constructor de Fases</span>
                    <span className="badge bg-light text-dark fs-6 shadow-sm">
                        ⏳ Tiempo Total: {totalGameDurationInSeconds} segs
                    </span>
                </div>
                <div className="card-body p-0" style={{ overflowX: 'auto' }}>
                    <table className="table table-bordered table-hover mb-0 text-center align-middle">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '80px' }}>Orden</th>
                                <th style={{ width: '130px' }}>Tipo</th>
                                <th style={{ width: '100px' }}>Duración (s)</th>
                                <th style={{ width: '90px' }}>Bombas</th>
                                <th>Velocidad de Aparición</th>
                                <th style={{ width: '70px' }}>Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {gamePhasesSequence.map((phaseData, index) => (
                                <tr key={phaseData.id}>
                                    <td>
                                        <div className="d-flex justify-content-center">
                                            <button className="btn btn-sm btn-light border me-1 py-0 px-1" onClick={() => movePhasePosition(index, -1)} disabled={index === 0}><i className="bi bi-chevron-up"></i></button>
                                            <button className="btn btn-sm btn-light border py-0 px-1" onClick={() => movePhasePosition(index, 1)} disabled={index === gamePhasesSequence.length - 1}><i className="bi bi-chevron-down"></i></button>
                                        </div>
                                    </td>
                                    <td>
                                        <select 
                                            className={`form-select form-select-sm font-weight-bold text-white ${phaseData.type === 'bonus' ? 'bg-warning border-warning text-dark' : 'bg-primary border-primary'}`}
                                            value={phaseData.type}
                                            onChange={(e) => updatePhaseProperty(phaseData.id, 'type', e.target.value)}
                                        >
                                            <option value="normal">NORMAL</option>
                                            <option value="bonus">BONUS</option>
                                        </select>
                                    </td>
                                    <td>
                                        <input type="number" className="form-control form-control-sm text-center font-weight-bold" value={phaseData.duration} min="1" onChange={(e) => updatePhaseProperty(phaseData.id, 'duration', parseInt(e.target.value) || 0)} />
                                    </td>
                                    <td>
                                        <input type="number" className="form-control form-control-sm text-center" value={phaseData.bombs} min="0" disabled={phaseData.type === 'bonus'} onChange={(e) => updatePhaseProperty(phaseData.id, 'bombs', parseInt(e.target.value) || 0)} />
                                    </td>
                                    <td>
                                        {phaseData.type === 'normal' ? (
                                            <div className="d-flex flex-column align-items-center">
                                                {/* Selector de nivel de velocidad */}
                                                <select 
                                                    className="form-select form-select-sm mb-1" style={{ maxWidth: '160px' }}
                                                    value={phaseData.speedLevel}
                                                    onChange={(e) => updatePhaseSpeedPreset(phaseData.id, e.target.value)}
                                                >
                                                    <option value="slow">{SPEED_PRESETS.slow.label}</option>
                                                    <option value="normal">{SPEED_PRESETS.normal.label}</option>
                                                    <option value="fast">{SPEED_PRESETS.fast.label}</option>
                                                    <option value="extreme">{SPEED_PRESETS.extreme.label}</option>
                                                    <option value="custom">{SPEED_PRESETS.custom.label}</option>
                                                </select>
                                                
                                                {/* Inputs manuales solo visibles si es 'custom' */}
                                                {phaseData.speedLevel === 'custom' && (
                                                    <div className="d-flex align-items-center mt-1">
                                                        <input type="number" className="form-control form-control-sm text-center px-1" style={{ width: '60px' }} title="Mínimo ms" value={phaseData.speedConfig.minTime} onChange={(e) => updateCustomSpeedValue(phaseData.id, 'minTime', parseInt(e.target.value) || 0)} />
                                                        <span className="mx-1 text-muted">-</span>
                                                        <input type="number" className="form-control form-control-sm text-center px-1" style={{ width: '60px' }} title="Máximo ms" value={phaseData.speedConfig.maxTime} onChange={(e) => updateCustomSpeedValue(phaseData.id, 'maxTime', parseInt(e.target.value) || 0)} />
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-muted small"><i className="bi bi-dash"></i></span>
                                        )}
                                    </td>
                                    <td>
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => removePhase(phaseData.id)} title="Eliminar Fase">
                                            <i className="bi bi-trash3-fill"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="card-footer bg-light d-flex flex-wrap justify-content-between align-items-center">
                    <button className="btn btn-outline-success btn-sm font-weight-bold my-1" onClick={addNewPhase}>
                        <i className="bi bi-plus-circle-fill me-1"></i> Agregar Fase
                    </button>
                    <div className="my-1">
                        <button className="btn btn-outline-secondary btn-sm me-2" onClick={loadDefaultPhases}>
                            <i className="bi bi-arrow-counterclockwise me-1"></i> Default
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={saveCustomPhasesToMemory}>
                            <i className="bi bi-floppy-fill me-1"></i> Guardar Fases
                        </button>
                    </div>
                </div>
            </div>

            {/* BLOQUE 3: RESUMEN Y BOTONERA MAESTRA */}
            <div className="row">
                {/* Controles hacia los Celulares */}
                <div className="col-md-6">
                    <div className="card shadow-sm mb-4 h-100 border-success">
                        <div className="card-header bg-success text-white font-weight-bold">
                            <i className="bi bi-phone-fill me-2"></i> 3. Lanzamiento a Celulares
                        </div>
                        <div className="card-body d-flex flex-column">
                            
                            {/* Resumen Estético */}
                            <div className="alert border-success bg-light pb-2 pt-3 mb-4 shadow-sm">
                                <h6 className="font-weight-bold text-success text-center border-bottom border-success pb-2 mb-3">
                                    <i className="bi bi-clipboard-check me-1"></i> RESUMEN DEL LANZAMIENTO
                                </h6>
                                <p className="mb-1"><i className="bi bi-record-circle-fill me-2 text-success"></i> <strong>Modo:</strong> {isPracticeRound ? 'Prueba (Sin premio)' : 'REAL (Premio $100)'}</p>
                                <p className="mb-1"><i className="bi bi-geo-alt-fill me-2 text-success"></i> <strong>Ganador:</strong> {selectedWinningZone === 'random' ? (calculatedRandomWinner ? calculatedRandomWinner.toUpperCase() : 'Al azar') : selectedWinningZone.toUpperCase()}</p>
                                <p className="mb-0"><i className="bi bi-stopwatch-fill me-2 text-success"></i> <strong>Duración:</strong> {totalGameDurationInSeconds} Segundos</p>
                            </div>

                            <button className="btn btn-outline-secondary mb-3 btn-lg font-weight-bold" 
                                onClick={() => triggerModal('¿Llamar la atención de la gente mostrando la portada de espera en sus dispositivos y pantalla?', executeDisplayFrontPage, 'Sí, mostrar portada')}>
                                <i className="bi bi-display me-2"></i> Mostrar Portada / Espera
                            </button>
                            
                            <button className="btn btn-success mb-4 btn-lg font-weight-bold shadow" style={{ fontSize: '1.2rem' }}
                                onClick={() => triggerModal('¿Estás seguro de INICIAR el juego en este momento con la configuración actual?', executeStartGame, '¡INICIAR JUEGO!', 'Cancelar')}>
                                <i className="bi bi-play-circle-fill me-2"></i> INICIAR JUEGO AHORA
                            </button>
                            
                            <hr className="w-100 mt-auto" />
                            
                            <div className="d-flex justify-content-between mt-3">
                                <button className="btn btn-danger font-weight-bold w-100 me-2" 
                                    onClick={() => triggerModal('¡CUIDADO! Esto cortará el juego a la mitad en todos los celulares. Nadie ganará. ¿Abortar?', executeStopGame, 'Sí, Abortar', 'Cancelar')}>
                                    <i className="bi bi-sign-stop-fill me-1"></i> Abortar Juego
                                </button>
                                <button className="btn btn-outline-danger font-weight-bold w-100 ms-2" title="Termina el juego y muestra promo a todos"
                                    onClick={() => triggerModal('¿Forzar el fin del juego y mostrar la Promoción en los celulares inmediatamente?', executeEmergencyPromo, 'Forzar Promo', 'Cancelar')}>
                                    <i className="bi bi-megaphone-fill me-1"></i> Promo Pánico
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Controles hacia la Pantalla Gigante */}
                <div className="col-md-6">
                    <div className="card shadow-sm mb-4 h-100 border-info">
                        <div className="card-header bg-info text-white font-weight-bold">
                            <i className="bi bi-tv-fill me-2"></i> 4. Controles Pantalla Gigante
                        </div>
                        <div className="card-body d-flex flex-column justify-content-center text-center">
                            <i className="bi bi-exclamation-triangle text-warning mb-2" style={{ fontSize: '3rem' }}></i>
                            <p className="text-muted mt-2 mb-4 px-3">
                                Presiona estos botones <strong>únicamente</strong> cuando estés seguro de que el tiempo de los celulares ya terminó o el cronómetro superior llegó a cero.
                            </p>
                            
                            <button className="btn btn-warning mb-3 btn-lg font-weight-bold mx-auto w-75 shadow-sm" 
                                onClick={() => {
                                    if(selectedWinningZone === 'random' && !calculatedRandomWinner && !window.confirm('No has iniciado el juego aún para calcular el ganador. ¿Seguro que quieres forzar esto?')) return;
                                    triggerModal(`Se enviará el gráfico de Ganador a la Pantalla Gigante.`, executeShowWinnerOnScreen, 'Mostrar Ganador');
                                }}>
                                <i className="bi bi-trophy-fill me-2"></i> Mostrar Ganador
                            </button>

                            <button className="btn btn-outline-info btn-lg font-weight-bold mx-auto w-75" 
                                onClick={() => triggerModal('¿Lanzar el gráfico de "Prueba Finalizada" a la pantalla gigante?', executePracticeFinishedOnScreen, 'Mostrar Prueba')}>
                                <i className="bi bi-flag-fill me-2"></i> Mostrar "Prueba Finalizada"
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* INTEGRACIÓN DEL MODAL ESTANDARIZADO */}
            {showConfirmModal && (
                <Confirm 
                    message={confirmModalData?.message}
                    actionToYesButton={confirmModalData?.actionToYesButton}
                    textYesButton={confirmModalData?.textYesButton}
                    textNoButton={confirmModalData?.textNoButton}
                    setShowModal={setShowConfirmModal}
                    setConfirmData={setConfirmModalData} 
                />
            )}
            
        </SecondaryBase>
    )
}

export { LuchoFest };


















//Funcional
// /**
//  * Componente LuchoFestAdmin
//  * Panel de control maestro para operar la dinámica Lucho Fest en vivo.
//  */
// const LuchoFest = ({ getOutOfTheDynamic }) => {
//     const { showMessage } = useFloatMessage();

//     // ==========================================
//     // 1. ESTADOS DE DATOS Y LOGÍSTICA
//     // ==========================================
//     const [activeZonesList, setActiveZonesList] = useState([]);
//     const [isLoadingZones, setIsLoadingZones] = useState(true);

//     const [isPracticeRound, setIsPracticeRound] = useState(true);
//     const [selectedWinningZone, setSelectedWinningZone] = useState('random');

//     // Estatus del sistema para retroalimentación visual del Admin
//     const [currentSystemStatus, setCurrentSystemStatus] = useState('Juego no iniciado. Configura la partida.');
//     const [calculatedRandomWinner, setCalculatedRandomWinner] = useState(null);

//     // ==========================================
//     // 2. ESTADOS DEL CONSTRUCTOR DE FASES
//     // ==========================================
//     const defaultPhasesSequence = [
//         { id: 1, type: 'normal', duration: 10, bombs: 2, speedConfig: { minTime: 800, maxTime: 1200, accelerationMultiplier: 0.95 } },
//         { id: 2, type: 'bonus', duration: 4, bombs: 0, speedConfig: { minTime: 800, maxTime: 1200, accelerationMultiplier: 1 } },
//         { id: 3, type: 'normal', duration: 12, bombs: 3, speedConfig: { minTime: 600, maxTime: 900, accelerationMultiplier: 0.90 } }
//     ];

//     const [gamePhasesSequence, setGamePhasesSequence] = useState(defaultPhasesSequence);

//     // ==========================================
//     // 3. ESTADOS PARA EL MODAL DE CONFIRMACIÓN
//     // ==========================================
//     const [showConfirmModal, setShowConfirmModal] = useState(false);
//     const [confirmModalData, setConfirmModalData] = useState(null);

//     // ==========================================
//     // 4. CARGA INICIAL
//     // ==========================================
//     useEffect(() => {
//         const fetchActiveZones = async () => {
//             try {
//                 const response = await fetch(`${process.env.REACT_APP_API_URL}admin/GetActiveZones`);
//                 if (response.ok) {
//                     const zones = await response.json();
//                     setActiveZonesList(zones);
//                 } else {
//                     showMessage({ type: MESSAGE_TYPE.WARNING, text: 'No se pudieron cargar las zonas activas.' });
//                 }
//             } catch (error) {
//                 console.error("Error al cargar zonas activas", error);
//                 showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de conexión al cargar las zonas.' });
//             } finally {
//                 setIsLoadingZones(false);
//             }
//         };

//         // Cargar fases guardadas previamente si existen
//         const savedCustomPhases = localStorage.getItem('luchoFestCustomPhases');
//         if (savedCustomPhases) {
//             setGamePhasesSequence(JSON.parse(savedCustomPhases));
//         }

//         fetchActiveZones();
//     }, []);

//     // Calcula el tiempo total sumando las duraciones de todas las fases
//     const totalGameDurationInSeconds = gamePhasesSequence.reduce((total, phase) => total + phase.duration, 0);

//     // ==========================================
//     // 5. MÉTODOS DEL CONSTRUCTOR DE FASES
//     // ==========================================
//     const updatePhaseProperty = (phaseIdToUpdate, propertyName, newValue) => {
//         setGamePhasesSequence(previousSequence => 
//             previousSequence.map(phase => 
//                 phase.id === phaseIdToUpdate ? { ...phase, [propertyName]: newValue } : phase
//             )
//         );
//     };

//     const updateSpeedConfigProperty = (phaseIdToUpdate, propertyName, newValue) => {
//         setGamePhasesSequence(previousSequence => 
//             previousSequence.map(phase => {
//                 if (phase.id === phaseIdToUpdate) {
//                     return { ...phase, speedConfig: { ...phase.speedConfig, [propertyName]: newValue } };
//                 }
//                 return phase;
//             })
//         );
//     };

//     const addNewPhase = () => {
//         const newPhase = { 
//             id: Date.now(), type: 'normal', duration: 10, bombs: 2, 
//             speedConfig: { minTime: 800, maxTime: 1200, accelerationMultiplier: 0.95 } 
//         };
//         setGamePhasesSequence([...gamePhasesSequence, newPhase]);
//     };

//     const removePhase = (phaseIdToRemove) => {
//         if (gamePhasesSequence.length <= 1) {
//             showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debe existir al menos una fase en el juego.' });
//             return;
//         }
//         setGamePhasesSequence(gamePhasesSequence.filter(phase => phase.id !== phaseIdToRemove));
//     };

//     const movePhasePosition = (currentIndex, direction) => {
//         if ((currentIndex === 0 && direction === -1) || (currentIndex === gamePhasesSequence.length - 1 && direction === 1)) return;
        
//         const newSequence = [...gamePhasesSequence];
//         const targetIndex = currentIndex + direction;
        
//         // Intercambiar posiciones
//         const temporaryPhase = newSequence[currentIndex];
//         newSequence[currentIndex] = newSequence[targetIndex];
//         newSequence[targetIndex] = temporaryPhase;
        
//         setGamePhasesSequence(newSequence);
//     };

//     const saveCustomPhasesToMemory = () => {
//         localStorage.setItem('luchoFestCustomPhases', JSON.stringify(gamePhasesSequence));
//         showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Configuración de fases guardada exitosamente.' });
//     };

//     const loadDefaultPhases = () => {
//         setGamePhasesSequence(defaultPhasesSequence);
//         localStorage.removeItem('luchoFestCustomPhases');
//         showMessage({ type: MESSAGE_TYPE.INFO, text: 'Se ha restaurado la configuración predeterminada.' });
//     };


//     // ==========================================
//     // 6. WRAPPERS PARA LA API (Para usar con el Modal Confirm)
//     // ==========================================
//     // El modal 'Confirm' espera que la función retorne { success: true/false, message: '...' }

//     const executeDisplayFrontPage = async () => {
//         try {
//             const response = await fetch(`${process.env.REACT_APP_API_URL}admin/displayFrontPageLuchoFest`, { method: 'POST' });
//             if (response.ok) {
//                 setCurrentSystemStatus('Mostrando pantalla de Portada/Espera ⏳');
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Portada enviada exitosamente.' });
//                 return { success: true };
//             }
//             return { success: false, message: 'Error en el servidor al enviar portada.' };
//         } catch (error) {
//             return { success: false, message: 'Error de conexión con la API.' };
//         }
//     };

//     const executeStartGame = async () => {
//         // Lógica del ganador al azar en el momento de iniciar
//         let finalWinner = selectedWinningZone;
//         let randomWinnerAssigned = null;

//         if (selectedWinningZone === 'random' && activeZonesList.length > 0) {
//             const randomIndex = Math.floor(Math.random() * activeZonesList.length);
//             finalWinner = activeZonesList[randomIndex];
//             finalWinner = finalWinner.toLowerCase().replace("zona:", "").trim(); 
//             randomWinnerAssigned = finalWinner;
//             setCalculatedRandomWinner(randomWinnerAssigned);
//         }

//         const configurationPayload = {
//             isPracticeRound: isPracticeRound,
//             winningZone: finalWinner,
//             gameSequence: gamePhasesSequence
//         };

//         try {
//             const response = await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/start`, {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify(configurationPayload)
//             });

//             if (response.ok) {
//                 const winnerText = randomWinnerAssigned ? `Ganador Aleatorio: ${randomWinnerAssigned.toUpperCase()}` : `Ganador: ${finalWinner.toUpperCase()}`;
//                 setCurrentSystemStatus(`Juego en curso 🟢 (${winnerText})`);
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡El juego ha comenzado en todas las zonas!' });
//                 return { success: true };
//             }
//             return { success: false, message: 'Error al intentar iniciar la partida.' };
//         } catch (error) {
//             return { success: false, message: 'Fallo de red al comunicar con el servidor.' };
//         }
//     };

//     const executeStopGame = async () => {
//         try {
//             const response = await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/stop`, { method: 'POST' });
//             if (response.ok) {
//                 setCurrentSystemStatus('Juego detenido o abortado 🛑');
//                 showMessage({ type: MESSAGE_TYPE.WARNING, text: 'El juego fue abortado. Pantallas en espera.' });
//                 return { success: true };
//             }
//             return { success: false, message: 'Error al intentar detener el juego.' };
//         } catch (error) {
//             return { success: false, message: 'Error de conexión.' };
//         }
//     };

//     const executeEmergencyPromo = async () => {
//         try {
//             const response = await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/emergencyPromo`, {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify({ targetZones: ["todas"] })
//             });
//             if (response.ok) {
//                 setCurrentSystemStatus('Mostrando promoción forzada 🎁');
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Promoción comercial disparada con éxito.' });
//                 return { success: true };
//             }
//             return { success: false, message: 'Error al lanzar la promoción.' };
//         } catch (error) {
//             return { success: false, message: 'Error de conexión.' };
//         }
//     };

//     const executeShowWinnerOnScreen = async () => {
//         let finalWinner = selectedWinningZone === 'random' ? (calculatedRandomWinner || 'N/A') : selectedWinningZone;
        
//         try {
//             const response = await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/screen/showWinner`, {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify({ winningZone: finalWinner })
//             });
//             if (response.ok) {
//                 setCurrentSystemStatus('Mostrando ganador en pantalla gigante 🏆');
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Gráfico de ganador enviado a la pantalla.' });
//                 return { success: true };
//             }
//             return { success: false, message: 'Error al enviar ganador a la pantalla.' };
//         } catch (error) {
//             return { success: false, message: 'Error de conexión.' };
//         }
//     };

//     const executePracticeFinishedOnScreen = async () => {
//         try {
//             const response = await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/screen/showPracticeFinished`, { method: 'POST' });
//             if (response.ok) {
//                 setCurrentSystemStatus('Mostrando Prueba Finalizada en pantalla 🏁');
//                 showMessage({ type: MESSAGE_TYPE.INFO, text: 'Mensaje de prueba finalizada enviado.' });
//                 return { success: true };
//             }
//             return { success: false, message: 'Error al enviar mensaje a la pantalla.' };
//         } catch (error) {
//             return { success: false, message: 'Error de conexión.' };
//         }
//     };

//     // ==========================================
//     // 7. GESTORES DEL MODAL DE CONFIRMACIÓN
//     // ==========================================
//     const triggerModal = (message, actionFunc, yesBtnText = 'Confirmar', noBtnText = 'Cancelar') => {
//         setConfirmModalData({
//             message: message,
//             actionToYesButton: actionFunc,
//             textYesButton: yesBtnText,
//             textNoButton: noBtnText
//         });
//         setShowConfirmModal(true);
//     };

//     const ReturnToDashboard = () => {
//         if (getOutOfTheDynamic) getOutOfTheDynamic();
//     };

//     // ==========================================
//     // RENDERIZADO DEL PANEL
//     // ==========================================
//     return (
//         <SecondaryBase actionToExecute={ReturnToDashboard}>
            
//             {/* Header y Estatus General */}
//             <div className="d-flex justify-content-between align-items-center mb-3">
//                 <p className='AdmRunToGoal__Title mb-0'>
//                     <i className="bi bi-balloon"></i> Lucho Fest
//                 </p>
//                 <div className="alert alert-info py-2 px-3 mb-0 font-weight-bold" style={{ fontSize: '0.9rem' }}>
//                     <i className="bi bi-broadcast me-2"></i> Status: {currentSystemStatus}
//                 </div>
//             </div>

//             {/* BLOQUE 1: CONFIGURACIÓN LOGÍSTICA */}
//             <div className="card shadow-sm mb-4 border-primary">
//                 <div className="card-header bg-primary text-white font-weight-bold">
//                     <i className="bi bi-gear-fill me-2"></i> 1. Logística de la Ronda
//                 </div>
//                 <div className="card-body">
//                     <div className="row">
//                         <div className="col-md-6 mb-3">
//                             <label className="font-weight-bold d-block">Tipo de Ronda</label>
//                             {isPracticeRound ? (
//                                 <>
//                                     <button type='button' className='Btn__Adsl--Red w-100 mb-2'><i className='bi bi-check-circle-fill me-1'></i> Ronda de prueba</button>
//                                     <button type='button' onClick={() => setIsPracticeRound(false)} className='Btn__Adsl--RedOutlineTransparent w-100'>Ronda real</button>
//                                 </>
//                             ) : (
//                                 <>
//                                     <button type='button' onClick={() => setIsPracticeRound(true)} className='Btn__Adsl--RedOutlineTransparent w-100 mb-2'>Ronda de prueba</button>
//                                     <button type='button' className='Btn__Adsl--Red w-100'><i className='bi bi-check-circle-fill me-1'></i> Ronda real (Premio)</button>
//                                 </>
//                             )}
//                         </div>

//                         <div className="col-md-6 mb-3">
//                             <label className="font-weight-bold">Elegir Zona Ganadora</label>
//                             {isLoadingZones ? (
//                                 <p className="text-muted small">Cargando zonas...</p>
//                             ) : (
//                                 <select 
//                                     className="form-select form-control" 
//                                     value={selectedWinningZone}
//                                     onChange={(event) => setSelectedWinningZone(event.target.value)}
//                                 >
//                                     <option value="random">🎲 Elegir al Azar (El sistema decide al iniciar)</option>
//                                     {activeZonesList.map((zoneItem, index) => (
//                                         <option key={index} value={zoneItem.replace("zona:", "")}>
//                                             {zoneItem.toUpperCase()}
//                                         </option>
//                                     ))}
//                                 </select>
//                             )}
//                             <small className="form-text text-muted mt-2">
//                                 <i className="bi bi-info-circle me-1"></i> Si eliges "Al Azar", el ganador se calculará en el momento de iniciar.
//                             </small>
//                         </div>
//                     </div>
//                 </div>
//             </div>

//             {/* BLOQUE 2: CONSTRUCTOR DINÁMICO DE FASES */}
//             <div className="card shadow-sm mb-4 border-dark">
//                 <div className="card-header bg-dark text-white font-weight-bold d-flex justify-content-between align-items-center">
//                     <span><i className="bi bi-stopwatch-fill me-2"></i> 2. Constructor de Fases</span>
//                     <span className="badge bg-light text-dark fs-6">
//                         ⏳ Tiempo Total: {totalGameDurationInSeconds} segs
//                     </span>
//                 </div>
//                 <div className="card-body p-0" style={{ overflowX: 'auto' }}>
//                     <table className="table table-bordered table-hover mb-0 text-center align-middle">
//                         <thead className="table-light">
//                             <tr>
//                                 <th style={{ width: '80px' }}>Orden</th>
//                                 <th style={{ width: '150px' }}>Tipo</th>
//                                 <th style={{ width: '100px' }}>Duración (s)</th>
//                                 <th style={{ width: '100px' }}>Bombas</th>
//                                 <th>Velocidad (ms)</th>
//                                 <th style={{ width: '80px' }}>Acción</th>
//                             </tr>
//                         </thead>
//                         <tbody>
//                             {gamePhasesSequence.map((phaseData, index) => (
//                                 <tr key={phaseData.id}>
//                                     <td>
//                                         <div className="d-flex justify-content-center">
//                                             <button className="btn btn-sm btn-light me-1" onClick={() => movePhasePosition(index, -1)} disabled={index === 0}><i className="bi bi-arrow-up"></i></button>
//                                             <button className="btn btn-sm btn-light" onClick={() => movePhasePosition(index, 1)} disabled={index === gamePhasesSequence.length - 1}><i className="bi bi-arrow-down"></i></button>
//                                         </div>
//                                     </td>
//                                     <td>
//                                         <select 
//                                             className={`form-select form-select-sm font-weight-bold text-white ${phaseData.type === 'bonus' ? 'bg-warning border-warning' : 'bg-primary border-primary'}`}
//                                             value={phaseData.type}
//                                             onChange={(e) => updatePhaseProperty(phaseData.id, 'type', e.target.value)}
//                                         >
//                                             <option value="normal">NORMAL</option>
//                                             <option value="bonus">BONUS</option>
//                                         </select>
//                                     </td>
//                                     <td>
//                                         <input type="number" className="form-control form-control-sm text-center" value={phaseData.duration} min="1" onChange={(e) => updatePhaseProperty(phaseData.id, 'duration', parseInt(e.target.value) || 0)} />
//                                     </td>
//                                     <td>
//                                         <input type="number" className="form-control form-control-sm text-center" value={phaseData.bombs} min="0" disabled={phaseData.type === 'bonus'} onChange={(e) => updatePhaseProperty(phaseData.id, 'bombs', parseInt(e.target.value) || 0)} />
//                                     </td>
//                                     <td>
//                                         {phaseData.type === 'normal' ? (
//                                             <div className="d-flex align-items-center justify-content-center">
//                                                 <input type="number" className="form-control form-control-sm text-center me-1" style={{ width: '70px' }} title="Mínimo ms" value={phaseData.speedConfig.minTime} onChange={(e) => updateSpeedConfigProperty(phaseData.id, 'minTime', parseInt(e.target.value) || 0)} />
//                                                 -
//                                                 <input type="number" className="form-control form-control-sm text-center ms-1" style={{ width: '70px' }} title="Máximo ms" value={phaseData.speedConfig.maxTime} onChange={(e) => updateSpeedConfigProperty(phaseData.id, 'maxTime', parseInt(e.target.value) || 0)} />
//                                             </div>
//                                         ) : (
//                                             <span className="text-muted small">N/A</span>
//                                         )}
//                                     </td>
//                                     <td>
//                                         <button className="btn btn-sm btn-outline-danger" onClick={() => removePhase(phaseData.id)} title="Eliminar Fase">
//                                             <i className="bi bi-trash-fill"></i>
//                                         </button>
//                                     </td>
//                                 </tr>
//                             ))}
//                         </tbody>
//                     </table>
//                 </div>
//                 <div className="card-footer bg-white d-flex justify-content-between align-items-center">
//                     <button className="btn btn-outline-success btn-sm font-weight-bold" onClick={addNewPhase}>
//                         <i className="bi bi-plus-circle-fill me-1"></i> Agregar Fase
//                     </button>
//                     <div>
//                         <button className="btn btn-outline-secondary btn-sm me-2" onClick={loadDefaultPhases}>
//                             <i className="bi bi-arrow-counterclockwise me-1"></i> Predeterminado
//                         </button>
//                         <button className="btn btn-primary btn-sm" onClick={saveCustomPhasesToMemory}>
//                             <i className="bi bi-floppy-fill me-1"></i> Guardar Personalizado
//                         </button>
//                     </div>
//                 </div>
//             </div>

//             {/* BLOQUE 3: RESUMEN Y BOTONERA MAESTRA */}
//             <div className="row">
                
//                 {/* Controles hacia los Celulares */}
//                 <div className="col-md-6">
//                     <div className="card shadow-sm mb-4 h-100 border-success">
//                         <div className="card-header bg-success text-white font-weight-bold">
//                             <i className="bi bi-phone-fill me-2"></i> 3. Lanzamiento a Celulares
//                         </div>
//                         <div className="card-body d-flex flex-column">
                            
//                             {/* Resumen del Lanzamiento */}
//                             <div className="alert alert-success border-success pb-2 pt-3 mb-4">
//                                 <h6 className="font-weight-bold text-success text-center border-bottom border-success pb-2 mb-3">RESUMEN DEL PRÓXIMO LANZAMIENTO</h6>
//                                 <p className="mb-1"><i className="bi bi-award-fill me-2 text-success"></i> <strong>Modo:</strong> {isPracticeRound ? 'Prueba (Sin premio)' : 'REAL (Premio $100)'}</p>
//                                 <p className="mb-1"><i className="bi bi-geo-alt-fill me-2 text-success"></i> <strong>Ganador:</strong> {selectedWinningZone === 'random' ? 'Se decidirá al azar' : selectedWinningZone.toUpperCase()}</p>
//                                 <p className="mb-0"><i className="bi bi-stopwatch-fill me-2 text-success"></i> <strong>Duración Total:</strong> {totalGameDurationInSeconds} Segundos</p>
//                             </div>

//                             <button className="btn btn-outline-secondary mb-3 btn-lg font-weight-bold" 
//                                 onClick={() => triggerModal('¿Llamar la atención de la gente mostrando la portada de espera en sus dispositivos y pantalla?', executeDisplayFrontPage, 'Sí, mostrar portada')}>
//                                 <i className="bi bi-display me-2"></i> Mostrar Portada / Espera
//                             </button>
                            
//                             <button className="btn btn-success mb-4 btn-lg font-weight-bold shadow-sm" style={{ fontSize: '1.2rem' }}
//                                 onClick={() => triggerModal('¿Estás seguro de INICIAR el juego en este momento con la configuración actual?', executeStartGame, '¡INICIAR JUEGO!', 'Cancelar')}>
//                                 <i className="bi bi-play-circle-fill me-2"></i> INICIAR JUEGO AHORA
//                             </button>
                            
//                             <hr className="w-100 mt-auto" />
                            
//                             <div className="d-flex justify-content-between mt-3">
//                                 <button className="btn btn-danger font-weight-bold w-100 me-2" 
//                                     onClick={() => triggerModal('¡CUIDADO! Esto cortará el juego a la mitad en todos los celulares. Nadie ganará. ¿Abortar?', executeStopGame, 'Sí, Abortar', 'Cancelar')}>
//                                     <i className="bi bi-sign-stop-fill me-1"></i> Abortar Juego
//                                 </button>
//                                 <button className="btn btn-outline-danger font-weight-bold w-100 ms-2" title="Termina el juego y muestra promo a todos"
//                                     onClick={() => triggerModal('¿Forzar el fin del juego y mostrar la Promoción en los celulares inmediatamente?', executeEmergencyPromo, 'Forzar Promo', 'Cancelar')}>
//                                     <i className="bi bi-megaphone-fill me-1"></i> Promo Pánico
//                                 </button>
//                             </div>
//                         </div>
//                     </div>
//                 </div>

//                 {/* Controles hacia la Pantalla Gigante */}
//                 <div className="col-md-6">
//                     <div className="card shadow-sm mb-4 h-100 border-info">
//                         <div className="card-header bg-info text-white font-weight-bold">
//                             <i className="bi bi-tv-fill me-2"></i> 4. Controles Pantalla Gigante
//                         </div>
//                         <div className="card-body d-flex flex-column justify-content-center text-center">
//                             <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
//                             <p className="text-muted mt-2 mb-4 px-3">
//                                 Presiona estos botones <strong>únicamente</strong> cuando estés seguro de que el tiempo de los celulares ya terminó (aprox. {totalGameDurationInSeconds + 3} segundos después de Iniciar).
//                             </p>
                            
//                             <button className="btn btn-warning mb-3 btn-lg font-weight-bold mx-auto w-75" 
//                                 onClick={() => {
//                                     if(selectedWinningZone === 'random' && !calculatedRandomWinner && !window.confirm('No has iniciado el juego aún para calcular el ganador. ¿Seguro que quieres forzar esto?')) return;
//                                     triggerModal(`Se enviará el gráfico de Ganador a la Pantalla Gigante.`, executeShowWinnerOnScreen, 'Mostrar Ganador');
//                                 }}>
//                                 <i className="bi bi-trophy-fill me-2"></i> Mostrar Ganador en Pantalla
//                             </button>

//                             <button className="btn btn-outline-info btn-lg font-weight-bold mx-auto w-75" 
//                                 onClick={() => triggerModal('¿Lanzar el gráfico de "Prueba Finalizada" a la pantalla gigante?', executePracticeFinishedOnScreen, 'Mostrar Prueba')}>
//                                 <i className="bi bi-flag-fill me-2"></i> Mostrar "Prueba Finalizada"
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             </div>

//             {/* INTEGRACIÓN DEL MODAL ESTANDARIZADO */}
//             {showConfirmModal && (
//                 <Confirm 
//                     message={confirmModalData?.message}
//                     actionToYesButton={confirmModalData?.actionToYesButton}
//                     textYesButton={confirmModalData?.textYesButton}
//                     textNoButton={confirmModalData?.textNoButton}
//                     setShowModal={setShowConfirmModal}
//                     setConfirmData={setConfirmModalData} // Limpia su propio estado al cerrar
//                 />
//             )}
            
//         </SecondaryBase>
//     )
// }

// export { LuchoFest };



















//Funcional
// /**
//  * Componente LuchoFestAdmin
//  * Panel de control para el operador. Permite configurar el juego y disparar los eventos
//  * hacia los dispositivos móviles y la pantalla gigante del estadio.
//  */
// const LuchoFest = ({ getOutOfTheDynamic }) => {
//     // ==========================================
//     // 1. ESTADOS DE CONFIGURACIÓN Y DATOS
//     // ==========================================
//     const [activeZonesList, setActiveZonesList] = useState([]);
//     const [isLoadingZones, setIsLoadingZones] = useState(true);

//     // Parámetros de logística de la ronda
//     const [isPracticeRound, setIsPracticeRound] = useState(true);
//     const [selectedWinningZone, setSelectedWinningZone] = useState('random'); // 'random' o el nombre de la zona

//     // Configuración del motor de fases (Pre-cargamos el esquema clásico de 3 fases)
//     const [gamePhasesSequence, setGamePhasesSequence] = useState([
//         { 
//             id: Date.now() + 1, type: 'normal', duration: 10, bombs: 2, 
//             speedConfig: { minTime: 800, maxTime: 1200, accelerationMultiplier: 0.95 } 
//         },
//         { 
//             id: Date.now() + 2, type: 'bonus', duration: 4, bombs: 0, 
//             speedConfig: { minTime: 800, maxTime: 1200, accelerationMultiplier: 1 } // El bonus no usa velocidad, pero se manda la estructura
//         },
//         { 
//             id: Date.now() + 3, type: 'normal', duration: 12, bombs: 3, 
//             speedConfig: { minTime: 600, maxTime: 900, accelerationMultiplier: 0.90 } 
//         }
//     ]);

//     // ==========================================
//     // 2. CARGA INICIAL DE DATOS
//     // ==========================================
//     useEffect(() => {
//         const fetchActiveZones = async () => {
//             try {
//                 // Llama al EndPoint que ya tienes en AdminController para traer las zonas
//                 const response = await fetch(`${process.env.REACT_APP_API_URL}admin/GetActiveZones`);
//                 if (response.ok) {
//                     const zones = await response.json();
//                     setActiveZonesList(zones);
//                 }
//             } catch (error) {
//                 console.error("Error al cargar zonas activas", error);
//             } finally {
//                 setIsLoadingZones(false);
//             }
//         };

//         fetchActiveZones();
//     }, []);

//     // ==========================================
//     // 3. MANEJADORES DE CONFIGURACIÓN (FASES)
//     // ==========================================
//     const updatePhaseProperty = (phaseId, propertyName, newValue) => {
//         setGamePhasesSequence(previousSequence => 
//             previousSequence.map(phase => 
//                 phase.id === phaseId ? { ...phase, [propertyName]: newValue } : phase
//             )
//         );
//     };

//     const updateSpeedConfigProperty = (phaseId, propertyName, newValue) => {
//         setGamePhasesSequence(previousSequence => 
//             previousSequence.map(phase => {
//                 if (phase.id === phaseId) {
//                     return {
//                         ...phase,
//                         speedConfig: { ...phase.speedConfig, [propertyName]: newValue }
//                     };
//                 }
//                 return phase;
//             })
//         );
//     };

//     // ==========================================
//     // 4. MANEJADORES DE ENDPOINTS (LA BATUTA)
//     // ==========================================

//     // Llama a los celulares y pantalla a la sala de espera
//     const handleDisplayFrontPage = async () => {
//         if (window.confirm('¿Mostrar portada/espera en dispositivos y pantalla?')) {
//             await fetch(`${process.env.REACT_APP_API_URL}admin/displayFrontPageLuchoFest`, { method: 'POST' });
//             alert("Portada enviada.");
//         }
//     };

//     // Inicia el juego con toda la configuración
//     const handleStartGame = async () => {
//         if (!window.confirm('¿Estás seguro de iniciar la dinámica ahora?')) return;

//         // Si eligió 'Al azar', calculamos un ganador aleatorio de la lista en este momento
//         let finalWinner = selectedWinningZone;
//         if (selectedWinningZone === 'random' && activeZonesList.length > 0) {
//             const randomIndex = Math.floor(Math.random() * activeZonesList.length);
//             finalWinner = activeZonesList[randomIndex];
//             // Lo quitamos de minúsculas y lo preparamos como espera el front
//             finalWinner = finalWinner.toLowerCase().replace("zona:", "").trim(); 
//         }

//         const configurationPayload = {
//             isPracticeRound: isPracticeRound,
//             winningZone: finalWinner,
//             gameSequence: gamePhasesSequence
//         };

//         try {
//             const response = await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/start`, {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify(configurationPayload)
//             });

//             if (response.ok) alert("¡Juego iniciado con éxito!");
//             else alert("Error al iniciar el juego.");
//         } catch (error) {
//             console.error(error);
//             alert("Excepción al intentar comunicar con el servidor.");
//         }
//     };

//     // Botón de Paro de Emergencia
//     const handleStopGame = async () => {
//         if (window.confirm('¡CUIDADO! Esto detendrá el juego a la mitad en todos los celulares. ¿Continuar?')) {
//             await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/stop`, { method: 'POST' });
//             alert("Juego detenido.");
//         }
//     };

//     // Botón de Pánico Comercial
//     const handleEmergencyPromo = async () => {
//         if (window.confirm('¿Forzar promoción comercial en los dispositivos ahora mismo?')) {
//             await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/emergencyPromo`, {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify({ targetZones: ["todas"] })
//             });
//             alert("Promoción enviada de emergencia.");
//         }
//     };

//     // Muestra al ganador en la Pantalla Gigante
//     const handleShowWinnerOnScreen = async () => {
//         let finalWinner = selectedWinningZone === 'random' ? '¡Zona Secreta (revisa celulares)!' : selectedWinningZone;
//         const targetZoneName = prompt("Ingresa el nombre de la zona a mostrar en pantalla (Ej. mobil, oro):", finalWinner);
        
//         if (targetZoneName) {
//             await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/screen/showWinner`, {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify({ winningZone: targetZoneName })
//             });
//             alert("Ganador mostrado en Pantalla Gigante.");
//         }
//     };

//     // Muestra el gráfico de 'Prueba Finalizada' en la Pantalla Gigante
//     const handleShowPracticeFinishedOnScreen = async () => {
//         if (window.confirm('¿Mostrar "Prueba Finalizada" en Pantalla Gigante?')) {
//             await fetch(`${process.env.REACT_APP_API_URL}admin/luchofest/screen/showPracticeFinished`, { method: 'POST' });
//             alert("Mensaje de prueba finalizada enviado a Pantalla Gigante.");
//         }
//     };

//     const ReturnToDashboard = () => {
//         try {
//             getOutOfTheDynamic()
//         } catch (error) {
            
//         }
//     }


//     return (
//         <SecondaryBase actionToExecute={ReturnToDashboard}>
//             {/* Header */}
//             <p className='AdmRunToGoal__Title'>
//                 <i className="bi bi-cake2"></i> Lucho Fest
//             </p>

//             {/* BLOQUE 1: CONFIGURACIÓN LOGÍSTICA */}
//             <div className="card shadow-sm mb-4">
//                 <div className="card-header bg-dark text-white font-weight-bold">
//                     1. Logística de la Ronda
//                 </div>
//                 <div className="card-body">
//                     <div className="row">
//                         <div className="col-md-6 mb-3">
//                             <p>Esta es una ronda: {isPracticeRound ? "Prueba" : "Real (Ganador recibe promo)"}</p>

//                             {isPracticeRound ? (
//                                 <>
//                                     <button type='button' className='Btn__Adsl--Red'><i className='bi bi-check-circle-fill'></i> Ronda de prueba</button>
//                                     <button type='button' onClick={() => setIsPracticeRound(false)} className='Btn__Adsl--RedOutlineTransparent mt-3'>Ronda real</button>
//                                 </>
//                             ) : (
//                                 <>
//                                     <button type='button' onClick={() => setIsPracticeRound(true)} className='Btn__Adsl--RedOutlineTransparent'>Ronda de prueba</button>
//                                     <button type='button' className='Btn__Adsl--Red mt-3'><i className='bi bi-check-circle-fill'></i> Ronda real</button>
//                                 </>
//                             )}
//                         </div>

//                         <div className="col-md-6 mb-3">
//                             <label className="font-weight-bold">Elegir Zona Ganadora</label>
//                             {isLoadingZones ? (
//                                 <p className="text-muted small">Cargando zonas...</p>
//                             ) : (
//                                 <select 
//                                     className="form-control" 
//                                     value={selectedWinningZone}
//                                     onChange={(event) => setSelectedWinningZone(event.target.value)}
//                                 >
//                                     <option value="random">🎲 Elegir al Azar (El sistema decide)</option>
//                                     {activeZonesList.map((zoneItem, index) => (
//                                         <option key={index} value={zoneItem.replace("zona:", "")}>
//                                             {zoneItem.toUpperCase()}
//                                         </option>
//                                     ))}
//                                 </select>
//                             )}
//                             <small className="form-text text-muted">
//                                 Al iniciar, los celulares de esta zona mostrarán el mensaje de victoria.
//                             </small>
//                         </div>
//                     </div>
//                 </div>
//             </div>

//             {/* BLOQUE 2: CONFIGURACIÓN DE FASES (EL MOTOR) */}
//             <div className="card shadow-sm mb-4">
//                 <div className="card-header bg-dark text-white font-weight-bold">
//                     2. Configuración de Tiempos y Fases
//                 </div>
//                 <div className="card-body p-0">
//                     <table className="table table-bordered table-striped mb-0">
//                         <thead className="thead-light">
//                             <tr>
//                                 <th># Fase</th>
//                                 <th>Tipo</th>
//                                 <th>Duración (seg)</th>
//                                 <th>Bombas</th>
//                                 <th>Velocidad (ms)</th>
//                             </tr>
//                         </thead>
//                         <tbody>
//                             {gamePhasesSequence.map((phaseData, index) => (
//                                 <tr key={phaseData.id}>
//                                     <td className="align-middle font-weight-bold">{index + 1}</td>
//                                     <td className="align-middle">
//                                         {/* <span className={`badge ${phaseData.type === 'bonus' ? 'badge-warning' : 'badge-primary'} p-2`}> */}
//                                         <span className={`p-2`}>
//                                             {phaseData.type.toUpperCase()}
//                                         </span>
//                                     </td>
//                                     <td>
//                                         <input 
//                                             type="number" 
//                                             className="form-control" 
//                                             value={phaseData.duration} 
//                                             min="1"
//                                             onChange={(e) => updatePhaseProperty(phaseData.id, 'duration', parseInt(e.target.value))}
//                                         />
//                                     </td>
//                                     <td>
//                                         <input 
//                                             type="number" 
//                                             className="form-control" 
//                                             value={phaseData.bombs} 
//                                             min="0"
//                                             disabled={phaseData.type === 'bonus'} // En bonus no hay bombas
//                                             onChange={(e) => updatePhaseProperty(phaseData.id, 'bombs', parseInt(e.target.value))}
//                                         />
//                                     </td>
//                                     <td>
//                                         {phaseData.type === 'normal' ? (
//                                             <div className="d-flex align-items-center">
//                                                 <input type="number" className="form-control form-control-sm mr-1" title="Milisegundos mínimos" value={phaseData.speedConfig.minTime} onChange={(e) => updateSpeedConfigProperty(phaseData.id, 'minTime', parseInt(e.target.value))} />
//                                                 -
//                                                 <input type="number" className="form-control form-control-sm mx-1" title="Milisegundos máximos" value={phaseData.speedConfig.maxTime} onChange={(e) => updateSpeedConfigProperty(phaseData.id, 'maxTime', parseInt(e.target.value))} />
//                                             </div>
//                                         ) : (
//                                             <span className="text-muted small">No aplica para bonus</span>
//                                         )}
//                                     </td>
//                                 </tr>
//                             ))}
//                         </tbody>
//                     </table>
//                 </div>
//             </div>

//             {/* BLOQUE 3: BOTONERA DE CONTROLES MAESTROS */}
//             <div className="row">
//                 {/* Controles hacia los Celulares */}
//                 <div className="col-md-6">
//                     <div className="card shadow-sm mb-4 h-100">
//                         <div className="card-header bg-primary text-white font-weight-bold">
//                             📱 Controles de Arena (Celulares)
//                         </div>
//                         <div className="card-body d-flex flex-column justify-content-center">
//                             <button className="btn btn-outline-secondary mb-3 btn-lg" onClick={handleDisplayFrontPage}>
//                                 1. Mostrar Portada / Espera
//                             </button>
//                             <button className="btn btn-success mb-3 btn-lg font-weight-bold" onClick={handleStartGame}>
//                                 2. INICIAR JUEGO AHORA
//                             </button>
                            
//                             <hr className="w-100" />
                            
//                             <button className="btn btn-danger mb-2" onClick={handleStopGame}>
//                                 🛑 DETENER / ABORTAR
//                             </button>
//                             <button className="btn btn-outline-danger" onClick={handleEmergencyPromo}>
//                                 🎁 FORZAR PROMOCIÓN (Pánico)
//                             </button>
//                         </div>
//                     </div>
//                 </div>

//                 {/* Controles hacia la Pantalla Gigante */}
//                 <div className="col-md-6">
//                     <div className="card shadow-sm mb-4 h-100">
//                         <div className="card-header bg-info text-white font-weight-bold">
//                             📺 Controles Pantalla Gigante
//                         </div>
//                         <div className="card-body d-flex flex-column justify-content-center">
//                             <p className="text-muted small text-center mb-4">
//                                 Presiona estos botones una vez que los celulares hayan terminado su cronómetro local.
//                             </p>
                            
//                             <button className="btn btn-warning mb-3 btn-lg font-weight-bold" onClick={handleShowWinnerOnScreen}>
//                                 🏆 Mostrar Ganador en Pantalla
//                             </button>

//                             <button className="btn btn-outline-info btn-lg" onClick={handleShowPracticeFinishedOnScreen}>
//                                 🏁 Mostrar "Prueba Finalizada"
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </SecondaryBase>
//     )
// }

// export { LuchoFest };











// {/* {!isPracticeRound ? (
// <button type='button' className='Btn__Adsl--Red'><i className='bi bi-check-circle-fill'></i> Ronda real</button>
// ) : (
// <button type='button' className='Btn__Adsl--RedOutlineTransparent'>Ronda real</button>
// )} */}

// {/* <label className="font-weight-bold">Tipo de Ronda</label>
// <div className="custom-control custom-switch mt-2">
// <input 
// type="checkbox" 
// className="custom-control-input" 
// id="practiceRoundSwitch" 
// checked={isPracticeRound}
// onChange={(event) => setIsPracticeRound(event.target.checked)}
// />
// <label className="custom-control-label text-warning font-weight-bold" htmlFor="practiceRoundSwitch">
// {isPracticeRound ? "Esta es una RONDA REAL (Ganador recibe promo)" : "Esta es una RONDA DE PRUEBA"}
// </label>
// </div> */}